import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import cn from 'classnames';
import dibsCss from 'dibs-css';

import { FormattedMessage } from 'dibs-react-intl';
import { SellerReviewsContent } from 'dibs-seller-reviews-modal';
import { PdpSharedMeetTheSeller } from '../PdpSharedMeetTheSeller/PdpSharedMeetTheSeller';
import { PdpSharedStorefrontLink } from '../shared/PdpSharedStorefrontLink/PdpSharedStorefrontLink';

import { type PdpRespSellerInformation_seller$key } from './__generated__/PdpRespSellerInformation_seller.graphql';
import { type PdpRespSellerInformation_user$key } from './__generated__/PdpRespSellerInformation_user.graphql';
import { type PdpRespSellerInformation_item$key } from './__generated__/PdpRespSellerInformation_item.graphql';

type Props = {
    seller: PdpRespSellerInformation_seller$key;
    user: PdpRespSellerInformation_user$key | null;
    item: PdpRespSellerInformation_item$key;
};

const sellerFragment = graphql`
    fragment PdpRespSellerInformation_seller on Seller {
        serviceId
        isAccessible
        ...PdpSharedStorefrontLink_seller
        ...PdpSharedMeetTheSeller_seller
        orderFeedbackPdp {
            metadata {
                aggregatedFeedback {
                    publicNoteTotalResults
                }
            }
        }
    }
`;

const userFragment = graphql`
    fragment PdpRespSellerInformation_user on User {
        ...PdpSharedMeetTheSeller_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespSellerInformation_item on Item {
        ...PdpSharedMeetTheSeller_item
    }
`;

export const PdpRespSellerInformation: FC<Props> = ({
    seller: sellerRef,
    user: userRef,
    item: itemRef,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const reviewCount =
        seller?.orderFeedbackPdp?.metadata?.aggregatedFeedback?.publicNoteTotalResults || 0;

    if (reviewCount === 0) {
        return (
            <>
                <PdpSharedMeetTheSeller seller={seller} user={user} item={item} />
                <div className={cn(dibsCss.wFull, dibsCss.textRight, dibsCss.mbMedium)}>
                    <PdpSharedStorefrontLink seller={seller} />
                </div>
            </>
        );
    }

    return (
        <section
            className={cn(dibsCss.bgWhite, dibsCss.ptXsmall, dibsCss.pbMedium)}
            data-tn="pdp-seller-info"
        >
            <div className={cn(dibsCss.flex, dibsCss.gapLarger, dibsCss.itemsStart)}>
                <div
                    className={cn(dibsCss.flex, dibsCss.flexCol, dibsCss.w400px, dibsCss.minW400px)}
                >
                    <PdpSharedMeetTheSeller seller={seller} user={user} item={item} />
                    <PdpSharedStorefrontLink seller={seller} />
                </div>
                <div className={cn(dibsCss.selfStretch, dibsCss.flex, dibsCss.itemsCenter)}>
                    <div className={cn(dibsCss.w1px, dibsCss.hFull, dibsCss.bgDolphin)} />
                </div>
                <div
                    className={cn(dibsCss.flex, dibsCss.flexCol, dibsCss.gapMedium, dibsCss.wFull)}
                >
                    <div className={dibsCss.sassyFontHeaderLarge}>
                        <FormattedMessage
                            id="PdpRespSellerInformation.sellerReviews"
                            defaultMessage="Seller Reviews"
                        />
                    </div>
                    {seller?.isAccessible && (
                        <SellerReviewsContent sellerId={seller?.serviceId || null} noTabs />
                    )}
                </div>
            </div>
        </section>
    );
};
